import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';

export type GrossTotalRange = { min: string; max: string };

/**
 * Euro amount typed by the user → cents, as stored in `grossAmount`. Empty input is no bound
 * (`undefined`); a negative or non-numeric amount is invalid (`null`).
 */
export function euroInputToCents(value: string): number | null | undefined {
	if (value.trim() === '') return undefined;
	const euros = Number(value);
	if (!Number.isFinite(euros) || euros < 0) return null;
	return Math.round(euros * 100);
}

type GrossTotalFilterProps = {
	value: GrossTotalRange;
	onChange: (value: GrossTotalRange) => void;
	error?: string;
};

/** Min/max gross total inputs in euros for the work packages list. */
export function GrossTotalFilter({ value, onChange, error }: GrossTotalFilterProps) {
	const { t } = useTranslation();
	const inputClassName = 'bg-input w-24 lg:w-28 h-8 lg:h-10 text-xs lg:text-sm';
	return (
		<div className="relative">
			<div className="flex items-center gap-2">
				<span className="text-xs lg:text-sm font-medium text-muted-foreground">{t('finance.list.grossTotal')}:</span>
				<Input
					type="number"
					inputMode="decimal"
					min={0}
					step="0.01"
					placeholder={t('finance.list.grossTotalMin')}
					aria-label={t('finance.list.grossTotalMinAria')}
					aria-invalid={!!error || undefined}
					className={inputClassName}
					value={value.min}
					onChange={(event) => onChange({ ...value, min: event.target.value })}
				/>
				<span className="text-muted-foreground">–</span>
				<Input
					type="number"
					inputMode="decimal"
					min={0}
					step="0.01"
					placeholder={t('finance.list.grossTotalMax')}
					aria-label={t('finance.list.grossTotalMaxAria')}
					aria-invalid={!!error || undefined}
					className={inputClassName}
					value={value.max}
					onChange={(event) => onChange({ ...value, max: event.target.value })}
				/>
			</div>
			{error && (
				<p role="alert" className="absolute top-full left-0 mt-1 whitespace-nowrap text-xs text-destructive">
					{error}
				</p>
			)}
		</div>
	);
}
